import { cloneDeep } from 'lodash/fp';
import _ from 'lodash';
import updater from '../../updater.js';
/**
 * This structure satisfies the basic structure requirements and also
 * 1) Contains a property 'selections' that contains the ids of selected nodes
 * 2) Contains a property 'defaultStyle' that contains the settings for default styles
 */
function getters(options) {
  return {
    /**
     * Return the selected ids
     * @return {array}    The selected ids
     */
    getSelections(state) {
      return cloneDeep(state.selections);
    },

    /**
     * Return whether the selection is empty
     * @return {boolean}      Whether the selection is empty or not
     */
    getIsSelectionEmpty(state) {
      return state.selections.length === 0;
    },

    /**
     * Return whether a node is selected
     * @param   {string}  id
     * @return  {boolean}     Whether it is selected or not
     */
    getIsSelected(state) {
      return id => {
        return state.selections.includes(id);
      };
    },

    /**
     * Return whether a container node is collapsed
     * @param   {string}  id
     * @return  {boolean}     Whether it is collapsed or not
     */
    getIsCollapsed(state) {
      return id => {
        return state.byId[id].collapsed;
      };
    },

    /**
     * Return the default style options
     * @return {Object}     The style options { type }
     */
    getDefaultStyle(state) {
      return state.defaultStyle || null;
    },
  };
}

/**
 * This structure contains actions that require basic structure requirements and
 * 1) Contain property 'selections' that contains the ids of the selected nodes
 * @param {Object}  options
 * @param {string}  options.type  The current type to be used in patch path
 */
function actions(options) {
  return {
    /**
     * Selects a node (of this type)
     * @param {string}  id        The node to be selected
     * @param {boolean} apply     Whether to apply the generated patch
     */
    select(context, { id, apply }) {
      // In case it is already selected do nothing
      const index = context.state.selections.indexOf(id);
      if (index !== -1) {
        return;
      }
      const patch = [
        // Push the node into selections, conditionally add property ignored
        {
          op: 'add',
          path: `/config/${options.type}/selections/-`,
          value: id,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Selects multiple nodes (of this type)
     * @param {array}   ids       The node ids to be selected
     * @param {boolean} apply     Whether to apply the generated patch
     */
    selectMany(context, { ids, apply }) {
      console.log('selectMany called for ids:', ids, 'options:', options);
      ids.forEach(id => context.dispatch('select', { id, apply: false }));
      updater.complete({ apply });
    },

    /**
     * Deselects a node (of this type)
     * @param {string}  id
     * @param {boolean} apply     Whether to apply the generated patch
     */
    deselect(context, { id, apply }) {
      // In case it is not selected do nothing
      const index = context.state.selections.indexOf(id);
      if (index === -1) {
        return;
      }
      const patch = [
        // Remove the node from selections
        {
          op: 'remove',
          path: `/config/${options.type}/selections/${index}`,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Deselect multiple nodes
     * @param {*} context
     * @param {*} param1
     */
    deselectMany(context, { ids, apply }) {
      const { selections } = context.state;
      // Deselect only selected ids and sort them by index in descending order
      ids
        .filter(id => selections.includes(id))
        .sort((a, b) => selections.indexOf(b) - selections.indexOf(a))
        .forEach(id => {
          context.dispatch('deselect', { id, apply: false });
        });
      updater.complete({ apply });
    },

    /**
     * Deselects all nodes (of this type)
     * @param {boolean} apply     Whether to apply the generated patch
     */
    deselectAll(context, { apply }) {
      const patch = [
        // Clear any selections
        {
          op: 'replace',
          path: `/config/${options.type}/selections`,
          value: [],
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Create a node
     * @param {Object}  node      The node to be created
     * @param {boolean} apply     Whether to apply the generated patch
     */
    create(context, { node, apply }) {
      const patch = [
        // Push the node
        {
          op: 'add',
          path: `/config/${options.type}/byId/${node.id}`,
          value: node,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Delete a node
     * @param {string}  id        The node to be deleted
     * @param {boolean} apply     Whether to apply the generated patch
     */
    delete(context, { id, apply }) {
      const patch = [
        // Remove node from byId structure
        {
          op: 'remove',
          path: `/config/${options.type}/byId/${id}`,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Set the collapsed state of a node
     * @param {string}  id        The node id
     * @param {boolean} value     The collapsed state
     * @param {boolean} apply     Whether to apply the generated patch
     */
    setCollapsed(context, { id, value, apply }) {
      const patch = [
        // Add component data to byId
        {
          op: 'replace',
          path: `/config/${options.type}/byId/${id}/collapsed`,
          value,
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },

    /**
     * Set default style settings (merges with old values)
     * @param {Object}  props          The style settings to change (one or more of { type })
     * @param {boolean} apply          Whether to apply the patch
     * @param {boolean} ignore         Whether the execution of the operation should be ignored by others
     */
    setDefaultStyle(context, { props, apply, ignore = true }) {
      // Merge with old values
      let { defaultStyle } = context.state;
      defaultStyle = { ...defaultStyle, ...props };
      const patch = [
        // Change style settings
        {
          op: 'replace',
          path: `/config/${options.type}/defaultStyle`,
          value: defaultStyle,
          ...(ignore && { ignore: true }),
        },
      ];
      updater.add(patch);
      updater.complete({ apply });
    },
  };
}

export default { getters, actions };
